import type { DrawNumber } from '../../../../../schema/draw/entry.js';
import type { Definition } from '../../../../../schema/frameworks/entry.js';
import { isCouponProduct, type Product, productSchema } from '../../../../../schema/product/entry.js';
import { BadRequestError } from '../../../../domain/entry.js';

function getTopptipsetProduct(
  subProduct?: string
): Extract<Product, 'TopptipsetStryk' | 'TopptipsetExtra' | 'Topptipset'> {
  if (subProduct === 'stryk') {
    return 'TopptipsetStryk';
  }
  if (subProduct === 'europa') {
    return 'TopptipsetExtra';
  }

  return 'Topptipset';
}

function getProduct(headLineItems: string[]): Product {
  const potentialProduct =
    headLineItems[0] === 'topptipset' ? getTopptipsetProduct(headLineItems[1]) : headLineItems[0];

  return productSchema.parse(potentialProduct);
}

function extractInteger(input: string): number | undefined {
  if (input.indexOf('=') === -1) {
    return undefined;
  }

  const [, value] = input.split('=');

  if (!value) {
    return undefined;
  }

  const intValue = Number.parseInt(value, 10);

  if (Number.isNaN(intValue)) {
    return undefined;
  }

  return intValue;
}

// TODO: move fn to definition or utilize definition instead
function isProductWithBetAmount(product: Product): boolean {
  switch (product) {
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
    case 'Powerplay':
    case 'Bomben':
      return true;
    default:
      return false;
  }
}

// TODO: is this needed? Move to definition ?
function isScoreboardProduct(product: Product): boolean {
  return product === 'Bomben';
}

function hasProductDrawNumber(product: Product): boolean {
  switch (product) {
    case 'Topptipset':
    case 'TopptipsetStryk':
    case 'TopptipsetExtra':
    case 'Bomben':
    case 'Powerplay':
      return true;
    default:
      return false;
  }
}

type BetPropertyData = {
  betAmount?: number;
  drawNumber?: DrawNumber;
  bombenNumber?: number;
  product: Definition['drawProducts'][number];
};

/**
 * Parses the headline from a external file.
 *
 * @param value headline string from file
 * @param product product fallback
 * @returns headline information
 * @throws {BadRequestError} if the headline is invalid or missing required information
 */
export function parseBetPropertyData(value: string, product?: Definition['drawProducts'][number]): BetPropertyData {
  const headLineItems = value.trim().toLowerCase().split(',');

  const productToUse = product ?? getProduct(headLineItems);

  if (!isCouponProduct(productToUse)) {
    throw new BadRequestError(`Not a valid product: ${productToUse}`);
  }

  const isScoreboard = isScoreboardProduct(productToUse);
  const hasBetAmount = isProductWithBetAmount(productToUse);
  const hasDrawNumber = hasProductDrawNumber(productToUse);

  let betAmount: BetPropertyData['betAmount'];
  let drawNumber: BetPropertyData['drawNumber'];
  let bombenNumber: BetPropertyData['bombenNumber'];
  for (const lineItem of headLineItems) {
    if (hasBetAmount && lineItem.indexOf('insats=') > -1) {
      betAmount = extractInteger(lineItem);
    }
    if (hasDrawNumber && lineItem.indexOf('omg=') > -1) {
      const number = extractInteger(lineItem);
      drawNumber = number === undefined ? number : (number as DrawNumber);
    }
    if (isScoreboard && lineItem.indexOf('bombennummer') > -1) {
      bombenNumber = extractInteger(lineItem);
    }
  }

  return {
    betAmount,
    bombenNumber,
    drawNumber,
    product: productToUse,
  };
}
